/**
 * The session panel's video call: the „Rozmowa wideo" switch, „Dołącz do rozmowy" with the host under
 * it, the Meet on its way, or the field a link is pasted into (consultation-calls plan, PR 2). What
 * stands is `model/callLink`'s `callPanel`; the writes are `useSessionCall`'s.
 *
 * The link opens a call to whoever holds it (R7), so the block is kept out of session replay.
 * The field takes focus when it opens; once it closes (or a link is removed) focus goes to the act
 * that opens it again, else the join link, else the block's heading — never to the page's top.
 */

import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { FormField } from '~/shared/ui/FormField';
import ink from '~/shared/ui/inkButton.module.css';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import { Toggle } from '~/shared/ui/Toggle';

import { useSessionCall } from '../lib/useSessionCall';
import type { BookingSession } from '../model/bookingSession';
import { CALL_LINK_ORIGIN_KEY } from '../model/callLink';
import compose from './ComposeForm.module.css';
import styles from './SidePanel.module.css';

const KEY = 'web:booking.call';

export function SessionCall({ session }: { readonly session: BookingSession }) {
  const { t } = useTranslation();
  const labelId = useId();
  const call = useSessionCall(session);
  const { panel, field, returnFocus, focusReturned } = call;
  const opener = useRef<HTMLButtonElement>(null);
  const joinLink = useRef<HTMLAnchorElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const fieldBox = useRef<HTMLFormElement>(null);
  const fieldOpen = field !== null;

  useEffect(() => {
    if (fieldOpen) fieldBox.current?.querySelector('input')?.focus();
  }, [fieldOpen]);

  useEffect(() => {
    if (!returnFocus) return;
    (opener.current ?? joinLink.current ?? heading.current)?.focus();
    focusReturned();
  }, [returnFocus, focusReturned]);

  if (panel.toggle === null && panel.link === null) return null;

  const linkActs = (
    <>
      <button
        ref={opener}
        type="button"
        className={styles.callAct}
        aria-disabled={call.saving ? 'true' : undefined}
        onClick={call.openField}
      >
        {t(panel.link?.kind === 'join' ? `${KEY}.change` : `${KEY}.add`)}
      </button>
      {panel.link?.kind === 'join' && panel.link.removable ? (
        <button type="button" className={styles.callAct} aria-disabled={call.saving ? 'true' : undefined} onClick={call.removeLink}>
          {t(`${KEY}.remove`)}
        </button>
      ) : null}
      {(panel.link?.kind === 'join' || panel.link?.kind === 'add') && panel.link.useMeet ? (
        <button type="button" className={styles.callAct} aria-disabled={call.saving ? 'true' : undefined} onClick={call.useMeet}>
          {t(`${KEY}.useMeet`)}
        </button>
      ) : null}
    </>
  );

  return (
    <section className={styles.call} aria-labelledby={labelId} data-clarity-mask="True">
      {panel.toggle === null ? (
        <h3 ref={heading} id={labelId} tabIndex={-1} className={styles.callLabel}>
          {t(`${KEY}.title`)}
        </h3>
      ) : (
        <div className={styles.callSwitch}>
          <h3 ref={heading} id={labelId} tabIndex={-1} className={styles.callLabel}>
            {t(`${KEY}.title`)}
          </h3>
          <Toggle
            checked={panel.toggle.on}
            label={t(`${KEY}.title`)}
            writesAtOnce
            lockedReason={call.saving ? t('web:booking.actions.saving') : null}
            onChange={call.setVideo}
          />
        </div>
      )}

      {panel.link?.kind === 'join' ? (
        <div className={styles.callJoin}>
          <a
            ref={joinLink}
            className={`${String(ink.ink)} ${String(styles.callJoinButton)}`}
            href={panel.link.link.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t(`${KEY}.joinLabel`, { host: panel.link.link.host })}
          >
            {t(`${KEY}.join`)}
          </a>
          <span className={styles.callHost}>
            {[panel.link.link.host, panel.link.origin === null ? null : t(CALL_LINK_ORIGIN_KEY[panel.link.origin])]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      ) : null}

      {panel.link?.kind === 'meetPending' ? <p className={styles.callNote}>{t(`${KEY}.meetPending`)}</p> : null}

      {field !== null ? (
        <form
          ref={fieldBox}
          className={styles.callField}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            call.saveLink();
          }}
        >
          <FormField
            label={t(`${KEY}.fieldLabel`)}
            value={field.value}
            placeholder="https://"
            error={field.problem}
            onChange={call.editField}
          />
          <p className={styles.callNote}>{t(call.helperKey)}</p>
          <div className={styles.callActs}>
            <button
              type="submit"
              className={`${String(ink.ink)} ${String(styles.callSave)}`}
              aria-disabled={call.saving ? 'true' : undefined}
            >
              {call.saving ? t('web:booking.actions.saving') : t(`${KEY}.save`)}
            </button>
            <button type="button" className={styles.callAct} onClick={call.closeField}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      ) : panel.link?.kind === 'join' && panel.link.changeable ? (
        <div className={styles.callActs}>{linkActs}</div>
      ) : panel.link?.kind === 'add' ? (
        <div className={styles.callActs}>{linkActs}</div>
      ) : null}

      {panel.link?.kind === 'add' && panel.link.connectHint && field === null ? (
        <p className={styles.callNote}>{t(`${KEY}.connectHint`)}</p>
      ) : null}

      {call.refusal === null ? null : (
        <p className={compose.refusal} role="alert">
          {call.refusal}
        </p>
      )}

      <ConfirmDialog prompt={call.prompt} onConfirm={call.confirm} onCancel={call.dismiss} />
      <LiveRegionAnnouncer message={call.announcement} />
    </section>
  );
}
